import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { OutcomeBadge, ALARM_LABEL, FEATURE_LABELS } from '@/components/testlab/shared';

const pct = (v) => (v == null ? '—' : `${Math.round(v * 100)} %`);
const num = (v, d = 1) => (v == null ? '—' : Number(v).toFixed(d).replace('.', ','));

export function FalseAlarmList({ data, onOpen }) {
  return (
    <div className="space-y-3" data-testid="false-alarm-list">
      {data.error && <p className="text-xs text-red-600" data-testid="false-alarm-error">Kunde inte hämta felklassificeringar: {data.error}</p>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Falska positiva</p><p className="text-2xl font-bold text-red-600" data-testid="fp-count">{data.false_positives ?? 0}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Falska negativa</p><p className="text-2xl font-bold text-amber-600" data-testid="fn-count">{data.false_negatives ?? 0}</p></CardContent></Card>
        <Card className="col-span-2"><CardContent className="p-3"><p className="text-xs text-muted-foreground">Produkter som orsakat falska positiva</p>
          <p className="text-sm font-semibold" data-testid="fp-products">{Object.entries(data.by_product || {}).map(([p, n]) => `${p}: ${n}`).join(' · ') || '—'}</p></CardContent></Card>
      </div>
      {!data.rows?.length ? <p className="text-sm text-muted-foreground">Inga felklassificeringar bland tester med loggade produktionsbeslut.</p> : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 text-left"><tr>
              {['Resultat', 'Produkt', 'Test / serie', 'Sensor', 'Max poäng', 'PM1/PM2.5/PM10', 'Tid till detektion', 'Sprayfilter', 'Larm', ''].map(c => <th key={c} className="px-2 py-2 font-semibold">{c}</th>)}
            </tr></thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.id} className="border-t hover:bg-muted/30" data-testid={`misclass-row-${r.id}`}>
                  <td className="px-2 py-2"><OutcomeBadge outcome={r.outcome} /></td>
                  <td className="px-2 py-2">{r.product}</td>
                  <td className="px-2 py-2"><div className="font-medium">{r.name}</div><div className="text-muted-foreground">{r.series}</div></td>
                  <td className="px-2 py-2 font-mono">{r.device_id?.slice(0, 8)}</td>
                  <td className="px-2 py-2 font-semibold">{pct(r.peak_score)}</td>
                  <td className="px-2 py-2 font-mono">{r.peak_pm1 ?? '—'} / {r.peak_pm2_5 ?? '—'} / {r.peak_pm10 ?? '—'}</td>
                  <td className="px-2 py-2">{r.seconds_to_detection != null ? `ca ${num(r.seconds_to_detection)} s` : '—'}</td>
                  <td className="px-2 py-2">{r.spray_filter_activated == null ? '—' : r.spray_filter_activated ? `Ja (${r.spray_filtered_readings})` : 'Nej'}</td>
                  <td className="px-2 py-2">{ALARM_LABEL[r.alarm_status]}</td>
                  <td className="px-2 py-2"><Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onOpen(r.id)} data-testid={`misclass-open-${r.id}`}>Analysera</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function MisclassificationDetail({ api, id, onClose }) {
  const [d, setD] = useState(null);
  const [note, setNote] = useState('');
  useEffect(() => { api(`sessions/${id}`).then(setD).catch(e => toast.error(e.message)); }, [api, id]);

  const save = async () => {
    try {
      const n = await api(`sessions/${id}/notes`, { method: 'POST', body: JSON.stringify({ text: note }) });
      setD(prev => ({ ...prev, notes: [...prev.notes, n] }));
      setNote('');
      toast.success('Anteckning sparad');
    } catch (e) { toast.error(e.message); }
  };

  const series = (d?.series || []).map(p => ({ ...p, time: p.t.slice(11, 19) }));
  const decisions = d?.decisions || [];
  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="misclass-detail">
        <DialogHeader><DialogTitle>{d?.row?.name || 'Analys'}</DialogTitle></DialogHeader>
        {!d ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : (
          <div className="space-y-4 text-xs">
            <div className="flex flex-wrap gap-2 items-center">
              <OutcomeBadge outcome={d.row.outcome} />
              <span>Facit: <strong>{d.row.ground_truth}</strong></span>
              <span>Produktionsbeslut: <strong>{d.row.decision || 'saknas'}</strong> (max {pct(d.row.peak_score)})</span>
              <span>{ALARM_LABEL[d.row.alarm_status]}</span>
            </div>
            <div className="h-56" data-testid="misclass-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} minTickGap={30} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  {d.event_markers.map((m, i) => <ReferenceLine key={i} x={String(m.timestamp).slice(11, 19)} stroke="#ef4444" strokeDasharray="4 2" />)}
                  <Line dataKey="pm1" stroke="#6366f1" dot={false} name="PM1" />
                  <Line dataKey="pm2_5" stroke="#ef4444" dot={false} name="PM2.5" />
                  <Line dataKey="pm10" stroke="#f59e0b" dot={false} name="PM10" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="misclass-features">
              {d.features?.available ? Object.entries(FEATURE_LABELS).map(([k, l]) => (
                <div key={k} className="rounded border p-2"><div className="text-muted-foreground">{l}</div><div className="font-mono font-semibold">{num(d.features[k], 3)}</div></div>
              )) : <p className="text-muted-foreground col-span-5">Signalvärden saknas: {d.features?.reason}</p>}
            </div>
            <p className="text-muted-foreground">Produktionsbeslut loggade: {decisions.length} · positiva (VAPE): {decisions.filter(x => x.status === 'vape').length} · sprayfiltrerade: {decisions.filter(x => x.spray_filtered).length}</p>
            <div className="space-y-2">
              <p className="font-semibold">Analysanteckningar</p>
              {d.notes.map((n, i) => <div key={i} className="rounded bg-muted/40 p-2"><span className="text-muted-foreground">{n.at.slice(0, 16)} · {n.by}:</span> {n.text}</div>)}
              <Textarea value={note} onChange={e => setNote(e.target.value)} placeholder="T.ex. Hårspray (enbart) klassades som VAPE – öppet larm uppdaterades." data-testid="misclass-note-input" />
              <Button size="sm" onClick={save} disabled={!note.trim()} data-testid="misclass-note-save">Spara anteckning</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
